/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AelNavigationParams,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { ScheduleTaskType } from '@gh/builder-shared-data';
import { ActionFlow, AppRouter, FormStateSnapshot, getRouteInitData } from '@gh/common-shared';
import {
  DialogService,
  noAnswer,
  NotificationService,
  yesAnswer,
  yesNoCancelConfirmation,
  yesNoConfirmation,
} from '@gh/core-ui';
import { openInNewWindow } from '@gh/core-util';
import { EMPTY$, merge$, of$ } from '@gh/rx';
import { filter$, switchMap$, tap$ } from '@gh/rx/operators';

import { Observable, Subject } from 'rxjs';

import { ScheduleDetailsBusinessService } from '../../resources/schedule-details-business.service';
import { ScheduleDetails, ScheduleMasterInfo, ScheduleTask } from '../../resources/schedule-details.model';
import { ScheduleDetailsFormState } from './schedule-details-form.service';

export interface ScheduleDetailsComponent {
  baseRoutePath: string;

  buildAelNavigationParams(): AelNavigationParams;
}

@Injectable()
export class ScheduleDetailsCommandService {
  private component: ScheduleDetailsComponent;
  private recalculate$$ = new Subject<void>();
  private reportUrl: string;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private navigations: BuilderNavigationService,
              public form: ScheduleDetailsFormState,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private business: ScheduleDetailsBusinessService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions) {
  }

  get completeFlow(): ActionFlow<FormFlowState<ScheduleDetails>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute((state) => this.business.save(state.value, this.form.mode)),
      finish: (state) => {
        if (this.form.createMode) {
          this.form.resetDirty();
          this.router.switchTo([this.component.baseRoutePath, state.value.scheduleId], {
            omitQueryParams: ['jobId', 'templateId'],
          });
        } else {
          this.form.build(state.value);
        }
      },
    });
  }

  init(component: ScheduleDetailsComponent): void {
    this.component = component;

    const { schedule, reportUrl } = getRouteInitData(this.route);

    this.reportUrl = reportUrl;
    this.form.build(schedule);

    this.recalculate$$.pipe(
      tap$(() => this.form.setPending(true)),
      switchMap$(() => this.business.recalculate(this.form.submitValue)))
      .subscribe(
        (loadedSchedule) => {
          this.form.setPending(false);
          this.form.mergeAfterRecalculate(loadedSchedule);
        },
        (error) => {
          this.form.setPending(false);
          this.notifications.processAll(error);
        });
  }

  setMasterInfo(snapshot: FormStateSnapshot<ScheduleMasterInfo>): void {
    this.form.setMasterInfo(snapshot);
  }

  setTaskField(task: ScheduleTask, field: string, value: any): void {
    this.form.setTaskField(task, field, value);
    if (field === 'isExcludeFromGanttChart') {
      this.recalculate();
    }
  }

  removeTask(task: ScheduleTask): void {
    // @ts-ignore
    this.dialogs.confirmation(yesNoConfirmation({
      [ScheduleTaskType.Summary]: 'msg_sch_sum_task_delete',
      [ScheduleTaskType.CostCenter]: 'msg_sch_task_delete',
      [ScheduleTaskType.Empty]: 'msg_sch_task_delete',
    }[task.type])).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.removeTask(task);
        this.recalculate();
      });
  }

  openTask(task: ScheduleTask): void {
    this.confirmNavigation()
      .subscribe(() => this.router.navigateToChild([
        this.component.baseRoutePath,
        this.form.scheduleId,
        'task',
        task.scheduleTaskId,
      ]));
  }

  saveBaseDates(): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_sch_overwrite_base_dates')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.saveBaseDates();
        this.recalculate();
      });
  }

  showBaseDatesChange(show: boolean): void {
    this.form.setShowBaseDates(show);
  }

  openGanttChart(): void {
    this.confirmNavigation()
      .subscribe(() => this.router.navigateToChild([
        this.component.baseRoutePath,
        this.form.scheduleId,
        'gantt-chart',
      ]));
  }

  openReport(): void {
    if (this.form.dirty) {
      this.notifications.error('msg_save_first');
      return;
    }
    openInNewWindow(this.reportUrl);
  }

  goToAel(): void {
    this.navigations.goToAel(this.component.buildAelNavigationParams());
  }

  private recalculate(): void {
    this.recalculate$$.next();
  }

  private confirmNavigation(): Observable<any> {
    if (this.form.createMode) {
      this.notifications.error('msg_save_first');
      return EMPTY$;
    } else if (this.form.dirty) {
      const confirmation$ = this.dialogs.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));

      const yes$ = confirmation$.pipe(
        filter$(yesAnswer),
        switchMap$(() => this.completeFlow.run({
          value: this.form.submitValue,
          form: this.form,
          trigger: 'process',
        }).toFinalState()),
        filter$((s) => !!s.success));

      const no$ = confirmation$.pipe(
        filter$(noAnswer),
        tap$(() => this.form.resetDirty()));

      return merge$(yes$, no$);
    } else {
      return of$(true);
    }
  }
}
